import React from "react";
import { useDarkMode } from "../../hooks/useDarkMode";
import { Link } from "react-router-dom";

const AboutDetails = () => {
  const { isDarkMode } = useDarkMode();

  /* ================= THEME ================= */

  const bgImage = "url('/Images/ABOUTPIC1.png')";
  const overlayBg = isDarkMode ? "bg-black/75" : "bg-black/60";

  const headingText = "text-white";
  const paraText = isDarkMode ? "text-gray-300" : "text-gray-200";

  return (
    <>
   <p className="sr-only">
Seo Up Rank is an advanced AI powered SEO automation platform that
provides website audits, keyword research, SEO analysis, and ranking
optimization tools. Visit the official platform at https://novarsis.ai/
to improve your website search engine rankings and organic traffic.
</p>
    <section
      aria-label="About Seo Up Rank AI SEO Tool"
      className="relative py-24 overflow-hidden bg-fixed bg-cover bg-center"
      style={{ backgroundImage: bgImage }}
    >

      {/* ================= STRUCTURED DATA ================= */}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Seo Up Rank",
            applicationCategory: "SEO Tool",
            operatingSystem: "Web",
            description:
              "AI powered SEO automation platform that analyzes ranking factors, performs technical SEO audits and optimizes websites automatically.",
            url: "https://novarsis.ai",
          }),
        }}
      />

      {/* ================= HIDDEN SEO TEXT ================= */}

      <p className="sr-only">
        Seo Up Rank is an AI powered SEO automation platform that improves
        website rankings, performs SEO audits, optimizes WordPress websites and
        helps businesses grow organic traffic using advanced SEO automation.
      </p>

      {/* ================= HIDDEN KEYWORDS ================= */}

      <p className="sr-only">
        AI SEO automation tool, website optimization platform, WordPress SEO
        automation, keyword ranking optimization software, technical SEO audit
        tool, AI SEO analytics platform.
      </p>

      {/* ================= INTERNAL LINKS FOR CRAWLERS ================= */}

      <div className="sr-only">
        <a href="/features">SEO Features</a>
        <a href="/tools">SEO Tools</a>
        <a href="/pricing">SEO Pricing</a>
        <a href="/blog">SEO Blog</a>
      </div>

      {/* ================= OVERLAY ================= */}

      <div className={`absolute inset-0 ${overlayBg}`}></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-16 flex flex-col lg:flex-row items-center gap-14">

        {/* ================= LEFT CONTENT ================= */}

        <article className="flex-1 relative border-l-4 border-[#008300] pl-6 transition-transform duration-500 hover:translate-x-2 text-center lg:text-left">

          {/* Small heading */}

          <p className="uppercase tracking-widest font-semibold mb-3 text-sm text-[#008300]">
            About Our SEO Tool
          </p>

          {/* Main Heading */}

          <h2
            className={`text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-6 ${headingText}`}
          >
            AI-Powered SEO Automation for
            <span className="relative inline-block text-[#008300]">
              {" "}Faster Website Growth
              <span className="absolute left-0 -bottom-2 w-24 h-[4px] bg-[#008300] rounded-full"></span>
            </span>
          </h2>

          {/* Hidden SEO Heading */}

          <h2 className="sr-only">
            AI powered SEO automation platform for website optimization
          </h2>

          {/* SEO Content */}

          <p
            className={`mb-6 leading-relaxed text-justify max-w-xl mx-auto lg:mx-0 ${paraText}`}
          >
            <strong>Seo Up Rank</strong> is an AI-powered SEO automation platform
            designed to simplify complex website optimization tasks. Our SEO
            software analyzes more than <strong>50+ ranking factors</strong>,
            detects technical issues, and automatically applies fixes to improve
            search engine rankings.
          </p>

          <p
            className={`mb-10 leading-relaxed text-justify max-w-xl mx-auto lg:mx-0 ${paraText}`}
          >
            From <strong>keyword optimization</strong> and
            <strong> technical SEO</strong> to
            <strong> WordPress SEO automation</strong>, Seo Up Rank helps
            businesses grow organic traffic faster while saving hours of manual
            SEO work.
          </p>

          {/* ================= PROGRESS BARS ================= */}

          <div className="space-y-6 mb-4 max-w-xl mx-auto lg:mx-0">

            <div>
              <div className="flex justify-between mb-2 text-sm font-semibold text-white">
                <span>AI SEO Automation</span>
                <span className="text-[#008300]">100%</span>
              </div>

              <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
                <div className="bg-[#008300] h-2 w-full rounded-full"></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2 text-sm font-semibold text-white">
                <span>WordPress SEO Optimization</span>
                <span className="text-[#008300]">95%</span>
              </div>

              <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
                <div className="bg-[#008300] h-2 w-[95%] rounded-full"></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2 text-sm font-semibold text-white">
                <span>Technical & On-Page SEO</span>
                <span className="text-[#008300]">100%</span>
              </div>

              <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
                <div className="bg-[#008300] h-2 w-full rounded-full"></div>
              </div>
            </div>

          </div>

        </article>

        {/* ================= RIGHT IMAGE ================= */}

        <div className="flex-1 flex justify-center relative">

          <figure className="relative group w-full max-w-xl">

            {/* Glow */}

            <div className="absolute -inset-6 bg-[#008300]/20 blur-3xl rounded-3xl"></div>

            <img
              src="/Images/AISeoAutomation.png"
              alt="Seo Up Rank AI SEO Automation Dashboard Interface"
              title="Seo Up Rank AI SEO Automation Tool"
              width="320"
              height="320"
              loading="lazy"
              className="relative w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 rounded-full object-cover shadow-2xl border-4 border-[#008300]/30 transform transition-transform duration-500 group-hover:scale-105"
            />

            {/* Image fallback */}

            <img
              src="/Images/ABOUTPIC1.png"
              alt="Seo Up Rank AI SEO automation background"
              className="hidden"
            />

            {/* Badge */}

            <figcaption className="absolute bottom-4 right-4 bg-[#008300] text-white rounded-lg shadow-xl px-5 py-3 flex items-center gap-3 transition hover:scale-105">

              <span className="text-xl">🚀</span>

              <p className="font-semibold text-sm sm:text-base">
                One-Click SEO Growth
              </p>

            </figcaption>

          </figure>

        </div>

      </div>

    </section> </>
  );
};

export default AboutDetails;